import React, { useEffect, useRef, useState } from "react";
import { useUploadFile } from "../hooks/use-upload-file";
import { useTemplate } from "../hooks/use-template";
import { useBroadcast } from "../hooks/use-broadcast";
import { BROADCAST_SEND_TYPE } from "../constants/broadcast";
import useTemplateMediaSelection, {
  TEMPLATE_MEDIA_STATUS,
} from "../../../../../hooks/useTemplateMediaSelection";
import { TemplateMediaValidationError } from "../../../../../utils/templateMediaValidation";
import { uploadFileViaPresignedPost } from "../../../../../utils/presignedUpload";
import { SelectTagWidgetContainer } from "../../BroadcastView.styles";
import {
  BroadcastAppCard,
  ContainerActions,
  DetailItem,
  DetailLabel,
  DetailsIcon,
  DetailsPanel,
  DetailsPanelTitle,
  DetailValue,
  ResumeContainer,
} from "../styles";
import { WhatsAppPreview } from "./whatsapp-preview";
import { CampaignUploadProgress } from "./campaign-upload-progress";
import { BackLink } from "../../Campaigns.style";
import Button from "../../../../../components/shared/components/button/Button.style";

export const ResumeBroadcast = ({ handleNext, handleBack, broadcastData }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [actionUploadStates, setActionUploadStates] = useState([]);
  const [templateUploaded, setTemplateUploaded] = useState(false);

  const {
    handleUploadFile,
    isLoading: isLoadingUploadFile,
    error: errorUploadFile,
    isUploadedFile,
  } = useUploadFile();

  const {
    status: mediaStatus,
    sample: mediaSample,
    error: mediaError,
    select: selectMedia,
    runCurrent: runCurrentMedia,
  } = useTemplateMediaSelection();

  const {
    createTemplate,
    isLoading: isLoadingTemplate,
    error: errorTemplate,
    isTemplateCreated,
  } = useTemplate({ resetKey: broadcastData?.templateFile });

  const {
    createBroadcast,
    isLoading: isLoadingBroadcast,
    error: errorBroadcast,
    isSentBroadcast,
  } = useBroadcast();

  const mountedRef = useRef(true);
  const submitLockRef = useRef(false);
  const advanceTimerRef = useRef(null);
  const seededRef = useRef(false);

  useEffect(
    () => () => {
      mountedRef.current = false;
      // A pending advance must not fire after this step unmounts: the wizard
      // would remount the resume step with a fresh (unlocked) send button.
      clearTimeout(advanceTimerRef.current);
    },
    [],
  );

  const {
    broadcastName,
    campaignCost,
    campaignCostData,
    phonesToNotify,
    contacts,
    sendType,
    scheduledDate,
    scheduledTime,
    bodyMessage,
    bodyMessageWithFormat,
    footerMessage,
    buttons,
    templateId,
    templateName,
    templateVariables,
    templateCategory,
    templateFile,
    templateFileUrl,
    templateFileType,
    actions,
    tagId,
    tagName,
  } = broadcastData;

  useEffect(() => {
    if (!actions) return;
    setActionUploadStates(
      actions.map((a) => ({ hasFile: Boolean(a.mediaFile), loading: false, done: false }))
    );
  }, [actions]);

  useEffect(() => {
    if (templateFileUrl) {
      setPreviewUrl(templateFileUrl);
      return;
    }
    if (!templateFile) return;
    const url = URL.createObjectURL(templateFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [templateFile]);

  // Seed and revalidate the shared hook from the current local File (F3):
  // only the new-local-sample branch (`!templateId && templateFile`) is
  // gated — existing templateId/remote-URL reuse never runs this.
  useEffect(() => {
    if (templateId || !templateFile || seededRef.current === templateFile) return;
    seededRef.current = templateFile;
    selectMedia(templateFile);
  }, [templateId, templateFile, selectMedia]);

  const uploadActionFile = async (file, index) => {
    setActionUploadStates((prev) =>
      prev.map((s, i) => (i === index ? { ...s, loading: true } : s))
    );
    const url = await handleUploadFile(file);
    setActionUploadStates((prev) =>
      prev.map((s, i) => (i === index ? { ...s, loading: false, done: Boolean(url) } : s))
    );
    return url;
  };

  const handleSubmit = async () => {
    if (submitLockRef.current) return;
    submitLockRef.current = true;
    let succeeded = false;
    try {
      window.scrollTo({ bottom: 0, behavior: "smooth" });

      let templateMediaUrl = templateFileUrl;
      let templateMediaFile = templateFile;

      if (!templateId && templateFile) {
        // F3: verify current validity before starting upload/creation —
        // never use a stale saved URL when a new local File is present.
        if (mediaStatus !== TEMPLATE_MEDIA_STATUS.VALID || !mediaSample) return;

        const outcome = await runCurrentMedia(async (currentSample, isCurrent) => {
          const { url } = await uploadFileViaPresignedPost(currentSample.file, {
            folder: "broadcast",
          });
          if (!isCurrent()) return null;
          if (!url)
            throw new TemplateMediaValidationError(
              "UPLOAD_MISSING_URL",
              "No pudimos subir el archivo. Vuelve a intentarlo.",
            );
          return { file: currentSample.file, url };
        });

        if (!outcome.started || !outcome.ok) return;

        templateMediaUrl = outcome.result.url;
        templateMediaFile = outcome.result.file;
        setTemplateUploaded(true);
      }

      if (!templateId) {
        const newTemplate = await createTemplate(
          {
            templateName,
            templateCategory,
            bodyMessage,
            templateFile: templateMediaFile,
            templateFileUrl: templateMediaUrl,
            templateVariables,
            footerMessage,
            templateButtons: buttons,
          },
          () => mountedRef.current,
        );
        if (!newTemplate) return;
      }

      let resolvedActions = actions ? [...actions] : [];

      const actionsWithFiles = resolvedActions
        .map((action, index) => ({ action, index }))
        .filter(({ action }) => action.mediaFile);

      if (actionsWithFiles.length > 0) {
        const uploadResults = await Promise.all(
          actionsWithFiles.map(({ action, index }) =>
            uploadActionFile(action.mediaFile, index).then((url) => ({ index, url }))
          )
        );
        uploadResults.forEach(({ index, url }) => {
          if (url) {
            resolvedActions[index] = { ...resolvedActions[index], mediaUrl: url };
          }
        });
      }

      const actionsPayload = resolvedActions.map(({ mediaFile, ...rest }) => rest);

      const newBroadcast = await createBroadcast({
        broadcastName,
        templateName,
        templateFileUrl: templateMediaUrl,
        sendType,
        scheduledDate,
        scheduledTime,
        contacts,
        actions: actionsPayload,
        phonesToNotify,
        templateNew: !templateId,
        tagId,
      });

      if (!newBroadcast) return;

      // Keep the submit lock held after a successful creation: this step is
      // about to advance and must never send the same campaign twice.
      succeeded = true;
      advanceTimerRef.current = setTimeout(() => handleNext(), 500);
    } finally {
      if (!succeeded) submitLockRef.current = false;
    }
  };

  const getScheduleText = () => {
    if (sendType === BROADCAST_SEND_TYPE.NOW) return "Envío inmediato";
    return `Programado para ${scheduledDate} a las ${scheduledTime}`;
  };

  const hasActionFiles = actionUploadStates.some((s) => s.hasFile);
  const isLoadingActionFiles = actionUploadStates.some((s) => s.loading);

  const isUploadingTemplateFile =
    !templateId && templateFile && mediaStatus === TEMPLATE_MEDIA_STATUS.SUBMITTING;

  const fileStatusText = () => {
    if (mediaStatus === TEMPLATE_MEDIA_STATUS.PENDING) return "Validando archivo de plantilla...";
    if (isUploadingTemplateFile && !isLoadingActionFiles) return "Subiendo archivo de plantilla...";
    if (templateUploaded && !isLoadingActionFiles) return "Archivo subido exitosamente";
    return "Subir archivo multimedia de plantilla";
  };

  const templateStatusText = () => {
    if (isLoadingTemplate) return "Creando plantilla de WhatsApp...";
    if (isTemplateCreated) return "Plantilla creada exitosamente";
    return "Crear plantilla de WhatsApp";
  };

  const broadcastStatusText = () => {
    if (isLoadingBroadcast) return "Creando campaña de difusión...";
    if (isSentBroadcast) return "Campaña creada exitosamente";
    return "Crear campaña de difusión";
  };

  const buttonSendText = () => {
    if (mediaStatus === TEMPLATE_MEDIA_STATUS.PENDING) return "Validando archivo...";
    if (isUploadingTemplateFile && !isLoadingActionFiles) return "Subiendo plantilla...";
    if (isLoadingActionFiles) {
      const doneCount = actionUploadStates.filter((s) => s.done).length;
      const totalCount = actionUploadStates.filter((s) => s.hasFile).length;
      return `Subiendo archivos (${doneCount}/${totalCount})...`;
    }
    if (isLoadingTemplate) return "Creando plantilla...";
    if (isLoadingBroadcast) return "Creando campaña...";
    if (isSentBroadcast) return "Campaña creada ✓";
    return "Enviar Campaña";
  };

  const error = mediaError?.message || errorUploadFile || errorTemplate || errorBroadcast;
  const isLoading =
    isLoadingUploadFile ||
    isLoadingTemplate ||
    isLoadingBroadcast ||
    isLoadingActionFiles ||
    isUploadingTemplateFile;
  const isCreated = isTemplateCreated || isSentBroadcast || isUploadedFile || templateUploaded;
  // Going back after a successful creation would unmount this step and
  // remount it unlocked, so the back link is blocked for the whole pause.
  const isBackBlocked = isLoading || isSentBroadcast;
  // F3: a required local sample that hasn't (re)validated as current yet
  // must not start upload/creation.
  const isMediaGateBlocking =
    !templateId && templateFile && mediaStatus !== TEMPLATE_MEDIA_STATUS.VALID;

  return (
    <SelectTagWidgetContainer>
      <BroadcastAppCard>
        <ResumeContainer>
          <DetailsPanel>
            <DetailsPanelTitle>
              <DetailsIcon />
              Detalles
            </DetailsPanelTitle>

            <DetailItem>
              <DetailLabel>Nombre de campaña</DetailLabel>
              <DetailValue>{broadcastName}</DetailValue>
            </DetailItem>

            <DetailItem>
              <DetailLabel>Programación</DetailLabel>
              <DetailValue>{getScheduleText()}</DetailValue>
            </DetailItem>

            <DetailItem>
              <DetailLabel>Plantilla</DetailLabel>
              <DetailValue>{templateName}</DetailValue>
            </DetailItem>

            <DetailItem>
              <DetailLabel>Contactos</DetailLabel>
              <DetailValue>{contacts.length}</DetailValue>
            </DetailItem>

            {tagName && (
              <DetailItem>
                <DetailLabel>Etiqueta a asignar</DetailLabel>
                <DetailValue>{tagName}</DetailValue>
              </DetailItem>
            )}

            <DetailItem>
              <DetailLabel>
                {campaignCostData?.provider === "TP"
                  ? "Comisión por servicio"
                  : "Costo estimado"}
              </DetailLabel>
              <DetailValue>${campaignCost} USD</DetailValue>
            </DetailItem>

            <DetailItem>
              <DetailLabel>Variables del mensaje</DetailLabel>
              {templateVariables && templateVariables.length > 0 ? (
                <DetailValue
                  style={{ display: "flex", flexDirection: "column", gap: "0.25rem", fontSize: "0.813rem" }}
                >
                  {templateVariables.map((variable, index) => (
                    <span
                      key={index}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        padding: "4px 8px",
                        backgroundColor: "#f0f9f7",
                        borderRadius: "4px",
                        border: "1px solid #128c7e",
                      }}
                    >
                      <strong style={{ color: "#128c7e", marginRight: "4px" }}>
                        Variable {index + 1}
                      </strong>
                      <span style={{ color: "#666" }}>→ {variable}</span>
                    </span>
                  ))}
                </DetailValue>
              ) : (
                <DetailValue>Sin variables</DetailValue>
              )}
            </DetailItem>

            {hasActionFiles && (
              <DetailItem>
                <DetailLabel>Archivos por botón</DetailLabel>
                <DetailValue style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                  {actions.map((action, i) =>
                    action.mediaFile ? (
                      <span
                        key={i}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "0.35rem",
                          padding: "4px 8px",
                          backgroundColor: "#f0f9f7",
                          borderRadius: "4px",
                          border: "1px solid #128c7e",
                          fontSize: "0.8rem",
                        }}
                      >
                        <strong style={{ color: "#128c7e" }}>Botón {i + 1}</strong>
                        <span style={{ color: "#555" }}>📎 {action.mediaFile.name}</span>
                      </span>
                    ) : null
                  )}
                </DetailValue>
              </DetailItem>
            )}
          </DetailsPanel>

          <WhatsAppPreview
            fileType={templateFileType}
            fileName={templateFile?.name || null}
            previewUrl={previewUrl}
            textWithFormat={bodyMessageWithFormat}
            footerMessage={footerMessage}
            buttons={buttons}
          />
        </ResumeContainer>

        {(isLoading || isCreated) && (
          <CampaignUploadProgress
            templateFile={templateFile}
            isUploadedFile={templateUploaded}
            isLoadingUploadFile={isUploadingTemplateFile}
            isLoadingActionFiles={isLoadingActionFiles}
            actionUploadStates={actionUploadStates}
            isTemplateCreated={isTemplateCreated}
            isLoadingTemplate={isLoadingTemplate}
            isSentBroadcast={isSentBroadcast}
            isLoadingBroadcast={isLoadingBroadcast}
            fileStatusText={fileStatusText}
            templateStatusText={templateStatusText}
            broadcastStatusText={broadcastStatusText}
          />
        )}

        <ContainerActions style={{ marginTop: "1rem" }}>
          <BackLink
            onClick={isBackBlocked ? undefined : handleBack}
            style={{
              pointerEvents: isBackBlocked ? "none" : "auto",
              opacity: isBackBlocked ? 0.5 : 1,
            }}
          >
            ← Atrás
          </BackLink>
          <Button
            designV4
            onClick={handleSubmit}
            disabled={
              isLoading || isSentBroadcast || isMediaGateBlocking
            }
          >
            {buttonSendText()}
          </Button>
        </ContainerActions>

        {error && (
          <div
            style={{
              marginTop: "1rem",
              padding: "1rem",
              backgroundColor: "#ffebee",
              border: "1px solid #f44336",
              borderRadius: "4px",
              color: "#c62828",
            }}
          >
            {error}
          </div>
        )}
      </BroadcastAppCard>
    </SelectTagWidgetContainer>
  );
};
